import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Link,
  Alert,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Container,
  Table,
  Badge,
} from '@cloudscape-design/components';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

function ModelStrategyPageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('model_strategy');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;

  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid gridDefinition={[{ colspan: { default: 12, s: 8 } }]}>
            <Box padding="s">
              <Box fontSize="display-l" fontWeight="bold" variant="h1" padding="n">
                Model Strategy
              </Box>
              <Box variant="p" color="text-body-secondary" margin={{ top: "xs", bottom: "l" }}>
                How ResearchIQ assigns different foundation models to different agents — 
                balancing quality, latency, and cost for a real-time demo experience.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">

        {/* Section 1: The Approach */}
        <div id="approach">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              The Approach
            </Header>
          </Box>

          <Container header={<Header variant="h2">🎯 Right Model for the Right Task</Header>}>
            <div style={{ borderLeft: `4px solid ${isDark ? '#60a5fa' : '#0073bb'}`, paddingLeft: '1.25rem' }}>
              <TextContent>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.7' }}>
                  Not every task in a research pipeline has the same requirements. A document retrieval 
                  task needs speed and instruction-following. A financial synthesis task needs structured 
                  reasoning. A formatting task needs consistency and low cost.
                </p>
                <p>
                  Rather than using a single model for everything, ResearchIQ assigns each agent the model 
                  best suited to its specific role. This is a <strong>configuration choice</strong> — the 
                  application code doesn't change when you swap models. Each agent reads its model from an 
                  environment variable at startup.
                </p>
              </TextContent>
            </div>
          </Container>

          <Box margin={{ top: "l" }}>
            <Alert statusIconAriaLabel="Info" header="Demo context">
              The model assignments shown here represent the combination we found works well for a 
              live demo — delivering high-quality, cited research outputs in under a minute. In a 
              production deployment, you might make different trade-offs: choosing higher-capability 
              models where latency is less critical, or optimising for cost at scale. The architecture 
              supports any combination without code changes.
            </Alert>
          </Box>
        </div>

        {/* Section 2: Model Assignment */}
        <div id="model-assignment">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" description="Current demo configuration" margin={{ bottom: "l" }}>
              Agent → Model Mapping
            </Header>
          </Box>

          <Table
            variant="embedded"
            columnDefinitions={[
              { id: 'agent', header: 'Agent', cell: i => <strong>{i.agent}</strong>, width: 200 },
              { id: 'model', header: 'Model', cell: i => i.model },
              { id: 'task', header: 'What It Does', cell: i => i.task },
              { id: 'why', header: 'Why This Model', cell: i => i.why },
            ]}
            items={[
              {
                agent: 'Document Researcher',
                model: 'Claude Haiku 4.5',
                task: 'Searches the Knowledge Base, extracts specific data points with citations',
                why: 'Fast response, strong instruction-following for retrieval tasks',
              },
              {
                agent: 'Evidence Compiler',
                model: 'Amazon Nova Pro',
                task: 'Correlates findings across documents, identifies patterns and gaps',
                why: 'Good reasoning at moderate speed, cost-effective for analysis',
              },
              {
                agent: 'Citation Tracker',
                model: 'Amazon Nova Pro',
                task: 'Formats the final research output with consistent citations',
                why: 'Reliable structured output, consistent formatting',
              },
              {
                agent: 'Insights Agent',
                model: 'Amazon Nova Pro',
                task: 'Cross-question theme detection, risk identification (runs async)',
                why: 'Runs in background after research completes — speed less critical',
              },
              {
                agent: 'Exec Brief (×4 parallel)',
                model: 'GPT-OSS 120B',
                task: 'Generates structured analyst note: Summary, SWO, Financials, Risk',
                why: 'Rich structured output (tables, matrices), strong financial reasoning',
              },
            ]}
          />
        </div>

        {/* Section 3: How It Works */}
        <div id="how-it-works">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              How It Works
            </Header>
          </Box>

          <TextContent>
            <p>
              Model selection is entirely configuration-driven. Each agent reads its model ID from an 
              environment variable — meaning you can change models without touching application code. 
              This makes it straightforward to test different combinations or adapt to new model releases.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="Environment Variable Configuration"
              variant="container"
              headerDescription="How models are assigned per agent at deployment time"
            >
              <pre style={{
                backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.85rem',
                overflow: 'auto',
                border: `1px solid ${isDark ? '#334155' : '#e9ebed'}`,
                lineHeight: '1.5',
              }}>
{`# Infrastructure default (guardrails, handshake, fallback)
STRANDS_MODEL_ID=amazon.nova-pro-v1:0

# Per-agent overrides
RESEARCH_MODEL_ID=au.anthropic.claude-haiku-4-5-20251001-v1:0
FORMATTING_MODEL_ID=amazon.nova-pro-v1:0
INSIGHTS_MODEL_ID=amazon.nova-pro-v1:0
EXEC_BRIEF_MODEL_ID=openai.gpt-oss-120b-1:0

# If an override isn't set, the agent falls back to STRANDS_MODEL_ID`}
              </pre>
            </ExpandableSection>
          </Box>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>Cross-Region Inference</h3>
                  <p>
                    Models are accessed via{' '}
                    <Link external href="https://docs.aws.amazon.com/bedrock/latest/userguide/geographic-cross-region-inference.html">
                      Amazon Bedrock Cross-Region Inference
                    </Link>{' '}
                    using AU geographic profiles. Requests are routed within the APAC region boundary — 
                    important for organisations with data residency requirements.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>No Vendor Lock-In</h3>
                  <p>
                    Because model selection is per-agent and configuration-driven, you're never locked 
                    into a single provider. As new models become available on Bedrock, you can evaluate 
                    and swap them in for specific agents without changing application code.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>
        </div>

        {/* Section 4: Iterative Testing */}
        <div id="what-we-tested">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Iterative Testing
            </Header>
          </Box>

          <TextContent>
            <p>
              Finding the right model combination was an iterative process. We tested different 
              configurations against our document corpus, evaluating output quality (citation accuracy, 
              structured formatting, factual grounding) alongside response time. The goal was to find 
              a combination that produces analyst-grade output within a timeframe that works for a 
              live interaction.
            </p>
            <p>
              This is the same process any team would follow when building a multi-agent system — 
              start with a baseline, identify which agents benefit most from specialised models, 
              and iterate until you find the right balance for your use case.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <Container header={<Header variant="h2">Key Learnings</Header>}>
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <TextContent>
                    <h3>What Mattered Most</h3>
                    <ul>
                      <li><strong>Research quality</strong> — citation accuracy and data extraction precision were non-negotiable</li>
                      <li><strong>Exec brief structure</strong> — tables, risk matrices, and formatted sections required a model with strong structured output</li>
                      <li><strong>Consistency</strong> — formatting agents needed to produce reliable, predictable output across runs</li>
                    </ul>
                  </TextContent>
                </div>
                <div>
                  <TextContent>
                    <h3>What We Could Trade Off</h3>
                    <ul>
                      <li><strong>Insights latency</strong> — runs async after research, so speed is less critical</li>
                      <li><strong>Formatting speed</strong> — a few extra seconds for consistent output is acceptable</li>
                      <li><strong>Model cost</strong> — at demo volumes, token cost is negligible vs quality impact</li>
                    </ul>
                  </TextContent>
                </div>
              </ColumnLayout>
            </Container>
          </Box>

          <Box margin={{ top: "l" }}>
            <TextContent>
              <p>
                In a production setting with different constraints — higher volume, stricter latency 
                SLAs, or deeper analytical requirements — you'd run a similar evaluation process 
                against your own corpus and quality criteria. The multi-model architecture makes 
                this experimentation straightforward: change an environment variable, redeploy, measure.
              </p>
            </TextContent>
          </Box>
        </div>

        {/* Section 5: Models on Bedrock */}
        <div id="models-on-bedrock">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Foundation Models on Amazon Bedrock
            </Header>
          </Box>

          <TextContent>
            <p>
              All models are accessed through{' '}
              <Link external href="https://aws.amazon.com/bedrock/">Amazon Bedrock</Link>
              {' '}— a single API for multiple model providers with consistent security, 
              governance, and billing. No separate API keys or vendor relationships to manage.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>Amazon Nova</h3>
                  <p>
                    AWS-built models (Micro, Lite, Pro, Premier) offering strong price-performance. 
                    Nova Pro is our infrastructure default — reliable, fast, and cost-effective for 
                    formatting and analysis tasks.
                  </p>
                  <h3>Anthropic Claude</h3>
                  <p>
                    Haiku 4.5 (fast, instruction-following), Sonnet 4.6 (balanced), Opus 4.7 (highest capability). 
                    Haiku handles our research retrieval — fast enough for real-time, precise enough for 
                    financial data extraction.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>Open-Source Models</h3>
                  <p>
                    GPT-OSS 120B delivers excellent structured output for our exec brief — tables, 
                    risk matrices, and formatted sections. Available on Bedrock alongside proprietary models.
                  </p>
                  <h3>The Flexibility</h3>
                  <p>
                    Meta Llama, Mistral, Cohere, and others are also available. As the model landscape 
                    evolves, you can evaluate new options per-agent without architectural changes. 
                    The multi-model approach means you're always using the best available tool for each job.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>
        </div>

      </SpaceBetween>
    </ContentLayout>
  );
}

function ModelStrategyPageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          { text: "The Approach", href: "#approach", level: 1 },
          { text: "Agent → Model Mapping", href: "#model-assignment", level: 1 },
          { text: "How It Works", href: "#how-it-works", level: 1 },
          { text: "Iterative Testing", href: "#what-we-tested", level: 1 },
          { text: "Models on Bedrock", href: "#models-on-bedrock", level: 1 },
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

function ModelStrategyPage() {
  return (
    <AppLayout
      content={<ModelStrategyPageContent />}
      tools={<ModelStrategyPageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default ModelStrategyPage;
